import * as React from "react";
import { useLingui } from "@lingui/react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ConfirmVariant = "default" | "destructive";

/**
 * Tracks one confirm action: pending from click until `onConfirm` settles,
 * resolving to whether it succeeded. The rejection is swallowed on purpose:
 * the caller (or the global mutation cache) owns the error message, and the
 * confirm UI only has to stay put so the user can retry.
 */
function useConfirmAction(
  onConfirm: () => Promise<unknown> | unknown,
  isPending: boolean | undefined
) {
  const [running, setRunning] = React.useState(false);
  // The state disables the button only after a re-render; the ref also stops
  // a fast double click from firing the action twice.
  const inFlight = React.useRef(false);
  const pending = running || !!isPending;

  const run = React.useCallback(async (): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setRunning(true);
    try {
      await onConfirm();
      return true;
    } catch {
      return false;
    } finally {
      inFlight.current = false;
      setRunning(false);
    }
  }, [onConfirm]);

  return { pending, run };
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel: React.ReactNode;
  /** Defaults to the shared "Cancel". */
  cancelLabel?: React.ReactNode;
  variant?: ConfirmVariant;
  /**
   * May return a promise. The dialog closes only when it resolves; on reject
   * it stays open and re-enables so the user can retry.
   */
  onConfirm: () => Promise<unknown> | unknown;
  /** External pending state, e.g. `mutation.isPending`, merged with the internal one. */
  isPending?: boolean;
  /** Keeps the confirm disabled, e.g. until a required reason is entered. */
  confirmDisabled?: boolean;
  /** Extra content between the description and the buttons (a reason field). */
  children?: React.ReactNode;
  className?: string;
}

/**
 * The confirmation step in front of a destructive or consequential action,
 * built to the "Destructive Actions" standard: controlled, a plain confirm
 * Button (never AlertDialogAction, which closes on click), close only on
 * success, and Cancel/Escape ignored while the action is in flight.
 *
 * Not for use inside an already-open Dialog: two stacked focus traps are
 * forbidden there. Use InlineConfirm in that dialog's footer instead.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = "default",
  onConfirm,
  isPending,
  confirmDisabled,
  children,
  className,
}: ConfirmDialogProps) {
  const { i18n } = useLingui();
  const { pending, run } = useConfirmAction(onConfirm, isPending);

  const handleOpenChange = (next: boolean) => {
    if (!next && pending) return;
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (pending) return;
    if (await run()) onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        className={className}
        // Radix warns about a missing description unless the link is removed.
        {...(description ? {} : { "aria-describedby": undefined })}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => handleOpenChange(false)}
          >
            {cancelLabel ?? i18n._({ id: "common.cancel", message: "Cancel" })}
          </Button>
          <Button
            type="button"
            variant={variant}
            disabled={pending || confirmDisabled}
            aria-busy={pending || undefined}
            onClick={() => void handleConfirm()}
          >
            {pending && <Loader2 className="animate-spin" aria-hidden="true" />}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export interface InlineConfirmProps {
  /** The question shown next to the buttons; also names the group for screen readers. */
  message?: React.ReactNode;
  confirmLabel: React.ReactNode;
  /** Defaults to the shared "Cancel". */
  cancelLabel?: React.ReactNode;
  variant?: ConfirmVariant;
  /**
   * May return a promise; pending until it settles, re-enabled on reject. On
   * success nothing is hidden here: the caller closes its dialog or resets.
   */
  onConfirm: () => Promise<unknown> | unknown;
  /** Leaves the confirm step, e.g. back to the regular dialog footer. */
  onCancel: () => void;
  isPending?: boolean;
  className?: string;
}

/**
 * The two-step confirm for an action inside an already-open Dialog, where a
 * nested modal AlertDialog is forbidden. Render it in place of the dialog
 * footer while a `showConfirm` state is set. Focus lands on Cancel, the safe
 * choice, because the button that opened this step has just unmounted.
 */
export function InlineConfirm({
  message,
  confirmLabel,
  cancelLabel,
  variant = "destructive",
  onConfirm,
  onCancel,
  isPending,
  className,
}: InlineConfirmProps) {
  const { i18n } = useLingui();
  const { pending, run } = useConfirmAction(onConfirm, isPending);
  const messageId = React.useId();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <div
      role="group"
      aria-labelledby={message ? messageId : undefined}
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end",
        className
      )}
    >
      {message && (
        <p id={messageId} className="text-sm text-muted-foreground sm:mr-auto">
          {message}
        </p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button
          ref={cancelRef}
          type="button"
          variant="outline"
          disabled={pending}
          onClick={onCancel}
        >
          {cancelLabel ?? i18n._({ id: "common.cancel", message: "Cancel" })}
        </Button>
        <Button
          type="button"
          variant={variant}
          disabled={pending}
          aria-busy={pending || undefined}
          onClick={() => {
            if (!pending) void run();
          }}
        >
          {pending && <Loader2 className="animate-spin" aria-hidden="true" />}
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
